import HyperDX from '@hyperdx/browser'
import hyperdxConfig from './hyperdxConfig.json'

interface HyperDXOptions {
  url: string
  apiKey: string
  urlMap?: Record<string, string>
}

interface HyperDXConfigRoot {
  onlineOptions: HyperDXOptions
  testOptions: HyperDXOptions
}

export function setupHyperDX(): void {
  const isProd = import.meta.env.MODE === 'production'
  const cfgRoot = hyperdxConfig as HyperDXConfigRoot
  const cfg = isProd ? cfgRoot.onlineOptions : cfgRoot.testOptions
  if (!cfg.url || !cfg.apiKey) return

  const lang = new URLSearchParams(window.location.search).get('lang') || ''
  const url = cfg.urlMap?.[lang] || cfg.url

  // 应用以 iframe 形式被父站（如 www.dvdfab.cn/member.htm）跨域嵌入。第三方上下文下 Chrome 的
  // storage partitioning 会让 OTel 的存储访问抛 SecurityError，而 `advancedNetworkCapture` 深度
  // 包裹 XHR，异常会顺着 send() 冒泡，把核心业务请求（/api/ticket/*）也一起拖挂成 "Network error"。
  // 因此仅在跨域嵌入时关闭深度抓包（第一方/同源继续保留完整能力，不损失可观测性）。
  const isCrossOriginEmbedded = (() => {
    try {
      return window.self !== window.top && window.top?.location.origin !== window.location.origin
    } catch {
      // 访问 window.top.location 抛 SecurityError 本身即说明跨域嵌入
      return true
    }
  })()

  HyperDX.init({
    url,
    apiKey: cfg.apiKey,
    service: isProd ? 'ticket-iframe' : 'ticket-iframe-dev',
    // OTel sdk-trace-web 的 propagation matcher 对整条 URL 走 `url.match(regex)`，
    // 必须能匹配 `https://host[/path][?query]` 形式。
    // 覆盖范围来自 .env.{production,test,development} 里实际使用的后端 host：
    //   - aiportrait.online: prod API + CDN（ticket-api / dl）
    //   - dvdfab.{cn,me,test}: test API（test-ticket-api.dvdfab.me）+ 历史 dvdfab 后端
    //   - localhost: 开发态
    // 第三方域（CDN / analytics）不在列表里，避免泄露内部 TraceId 拓扑。
    tracePropagationTargets: [
      /^http:\/\/localhost(?::\d+)?(?:\/|$)/,
      /^https?:\/\/(?:[^/?#]+\.)?aiportrait\.online(?::\d+)?(?:\/|$)/i,
      /^https?:\/\/(?:[^/?#]+\.)?dvdfab\.(?:cn|me|test)(?::\d+)?(?:\/|$)/i
    ],
    consoleCapture: isProd,
    advancedNetworkCapture: !isCrossOriginEmbedded
  })

  // 字段命名遵循 OTel semantic conventions：
  // - device.type 优先 mobile/tablet/desktop 枚举；非移动统一记 desktop
  // - os.name 用自由文本即可；navigator.userAgentData 是新标准、navigator.platform 已被 W3C 标记 deprecated
  //   但 Safari 仍只支持后者，按"优先新 API、老 API 回落"组合
  const uaPlatform =
    (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ||
    navigator.platform
  HyperDX.setGlobalAttributes({
    'device.type': /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
    'os.name': uaPlatform
  })
}
